import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface BreakTheIceContact {
  id: string;
  contact: {
    id: string;
    first_name: string;
    last_name: string;
    work_email: string;
    personal_email: string;
    company: {
      id: string;
      name: string;
    };
  };
  due_date?: string;
}

export function useBreakTheIceContacts() {
  const [contacts, setContacts] = useState<BreakTheIceContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchContacts() {
      try {
        // First, get the IDs of contacts with interactions
        const timelineQuery = supabase
          .from('nrm_timeline_entries')
          .select('contact_id')
          .not('contact_id', 'is', null);

        const communicationQuery = supabase
          .from('nrm_communication_logs')
          .select('contact_id')
          .not('contact_id', 'is', null);

        const followUpsQuery = supabase
          .from('nrm_follow_up_reminders')
          .select('contact_id')
          .not('contact_id', 'is', null);

        const [timelineResult, communicationResult, followUpsResult] = await Promise.all([
          timelineQuery,
          communicationQuery,
          followUpsQuery
        ]);

        const timelineContactIds = (timelineResult.data || []).map(t => t.contact_id);
        const communicationContactIds = (communicationResult.data || []).map(c => c.contact_id);
        const followUpContactIds = (followUpsResult.data || []).map(f => f.contact_id);

        // Get all contact IDs that have any interactions
        const contactIdsWithInteractions = [...new Set([...timelineContactIds, ...communicationContactIds])];
        const contactIdsWithFollowUps = [...new Set(followUpContactIds)];

        // Now get the contacts that don't have any interactions
        const { data, error: fetchError } = await supabase
          .from('nrm_contacts')
          .select(`
            id,
            first_name,
            last_name,
            work_email,
            personal_email,
            company:company_id (
              id,
              name
            )
          `);

        if (fetchError) throw fetchError;

        // Filter the contacts in memory instead
        const filteredContacts = (data || []).filter(contact => 
          !contactIdsWithInteractions.includes(contact.id) && 
          !contactIdsWithFollowUps.includes(contact.id)
        );

        const formattedContacts = filteredContacts.map(contact => ({
          id: contact.id,
          contact: {
            id: contact.id,
            first_name: contact.first_name,
            last_name: contact.last_name,
            work_email: contact.work_email,
            personal_email: contact.personal_email,
            company: contact.company,
          },
        }));

        setContacts(formattedContacts);
      } catch (err) {
        console.error('Error fetching break the ice contacts:', err);
        setError('Failed to load contacts');
      } finally {
        setLoading(false);
      }
    }

    fetchContacts();
  }, []);

  return { contacts, loading, error };
} 